import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { SET_PRODUCT_ENABLED } from '../../graphql/mutations';
import { money, useTinycmsPlans } from '../account/tinycmsPlans';
import { useAddons } from './addons';
import { SectionLocked, SectionShell } from './SectionShell';
import { useSeesExtractedDetail } from '../settings/supportAccess';
import { showInkToast } from './InkToast';
import { CAMPAIGN_LIST } from '../reach/campaignCards';
import { AgentFirstButton } from './AskYourAgent';
import { PRISM_COVERAGE, PrismCoverage, prismCoverageWords } from './prismCoverage';

/**
 * The two areas that sit beside Today and Content in the top bar ("tinycms UX
 * optimization review v6", Prototype v6), replacing Add-ons:
 *   Prism AI Research · Write. Comes with Pro since 2026-10-21; the
 *            owner can switch it off and back on. (Visuals stays hidden until
 *            it ships: no "not available yet" messaging for Prism AI.)
 *            (Called Atomize until 2026-10-19; the product id is still atomizer.)
 *   Reach    Calendar · Campaigns · Channels. Comes with Pro.
 * Sites without one see what it does and, if they own the site, the way to Pro.
 * The screens inside are the existing ones.
 */

// Reader questions need Prism AI: turning it off stops them on the site.
const READER_CHAT_ON = gql`
  query PrismReaderChatOn($accountId: ID!) {
    readerChat(accountId: $accountId) {
      eligible
      enabled
    }
  }
`;

/** "See Pro" and its price for the site owner; who can change the plan for anyone else. */
export function SeeProCta() {
  const navigate = useNavigate();
  const { isOwner, ownerEmail } = useAddons();
  const { plans } = useTinycmsPlans();
  const pro = plans.find((p) => p.key === 'pro');
  return isOwner ? (
    <div className="ink-locked-cta">
      <button
        type="button"
        className="ink-btn ink-btn-primary"
        onClick={() => navigate('/settings/billing')}
      >
        See Pro
      </button>
      {pro ? (
        <span className="ink-muted">{money(pro.yearlyPrice)} a month, billed yearly</span>
      ) : null}
    </div>
  ) : (
    <p className="ink-muted">
      Only the site owner{ownerEmail ? `, ${ownerEmail},` : ''} can change the plan.
    </p>
  );
}

/**
 * Turning Prism AI off and on for the site (the owner's call): Prism AI
 * settings' status card, and "Turn on" where it's off. chatOn: reader
 * questions are on, so turning it off stops them on the site too.
 */
export function usePrismSwitch() {
  const { accountId, refetchAccounts } = useAccount();
  const { atomizeOn, isOwner } = useAddons();
  // Facts are Technical support's, so the turn-off words never mention them
  // (backlog de4537df); what readers see changes only when reader questions are on.
  const { data: chatData } = useQuery<{ readerChat: { eligible: boolean; enabled: boolean } }>(
    READER_CHAT_ON,
    { variables: { accountId }, skip: !accountId || !atomizeOn || !isOwner }
  );
  const chatOn = Boolean(chatData?.readerChat?.eligible && chatData.readerChat.enabled);
  const [setEnabled, { loading: switching }] = useMutation(SET_PRODUCT_ENABLED);
  const [error, setError] = useState('');
  const switchTo = async (enabled: boolean): Promise<void> => {
    setError('');
    try {
      await setEnabled({ variables: { accountId, product: 'atomizer', enabled } });
      await refetchAccounts();
      if (!enabled) {
        showInkToast(
          chatOn
            ? 'Prism AI is off for this site, and so are reader questions.'
            : 'Prism AI is off for this site.',
          {
            label: 'Undo',
            onClick: () => void switchTo(true),
          }
        );
      } else {
        showInkToast(
          atomizeOn
            ? 'Prism AI will read what you publish, including what’s already on the site.'
            : 'Prism AI is on, and it will read what you publish.'
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Prism AI couldn’t be changed.');
    }
  };
  return { chatOn, switching, error, switchTo };
}

export function AtomizeSection({ children }: { children: React.ReactNode }) {
  const { accountId } = useAccount();
  const { atomizeOn, atomizeHeld, isOwner } = useAddons();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { data: coverageData } = useQuery<{
    researchOverview: PrismCoverage;
  }>(PRISM_COVERAGE, {
    variables: { accountId },
    skip: !accountId || !atomizeOn,
    fetchPolicy: 'cache-and-network',
  });
  const cov = coverageData?.researchOverview;
  // How many facts were extracted: TinyCMS technical support only (supportAccess.tsx).
  const seesExtracted = useSeesExtractedDetail(accountId);
  // Top right: what's read and why the rest isn't (prismCoverage.ts).
  const coverage = cov ? prismCoverageWords(cov, seesExtracted) : null;
  const { switching, error, switchTo } = usePrismSwitch();

  return (
    <SectionShell
      title="Prism AI"
      tabs={
        atomizeOn
          ? [
              { label: 'Research', to: '/prism-ai/research' },
              { label: 'Write', to: '/prism-ai/write' },
            ]
          : []
      }
      // Settings, top right as on Content (Prototype › Prism AI › Settings,
      // backlog 8938bafb): Turn off lives there now, with the rest of Prism AI.
      right={
        atomizeHeld ? (
          <span className="ink-prism-right">
            {atomizeOn && coverage ? <span className="ink-prism-coverage">{coverage}</span> : null}
            <button
              type="button"
              className="ink-btn"
              onClick={() =>
                navigate('/prism-ai/settings', { state: { from: `${pathname}${search}` } })
              }
            >
              Settings
            </button>
          </span>
        ) : null
      }
    >
      {error ? <p className="ink-section-error">{error}</p> : null}
      {atomizeOn ? (
        children
      ) : (
        <div className="ink-section-pad">
          {atomizeHeld ? (
            // Switched off by the owner (Prototype, round 13): a short card, and
            // only the owner gets the button.
            <SectionLocked
              title="Prism AI is off for this site"
              lede="Turn it back on to use Research and Write again."
              points={[]}
            >
              {isOwner ? (
                <button
                  type="button"
                  className="ink-btn ink-btn-primary"
                  disabled={switching}
                  onClick={() => void switchTo(true)}
                >
                  Turn on
                </button>
              ) : null}
            </SectionLocked>
          ) : (
            <SectionLocked
              title="Prism AI comes with Pro"
              lede="It reads your published articles and pulls out the facts in them, and the people, teams and places, so your readers and your agent can use what you’ve already reported."
              points={[
                'Reader questions: visitors ask, and get answers only from your published pages, with links',
                'Research: ask your archive a question and get a cited answer',
                'Write: new drafts built only from facts you’ve published',
              ]}
            >
              <SeeProCta />
            </SectionLocked>
          )}
        </div>
      )}
    </SectionShell>
  );
}

/**
 * Campaigns → "New campaign ▾", top right (designer's notes, 2026-10-25): ask
 * your agent, or set one up yourself (on the Campaigns screen through
 * ?new=dated, which ReachCampaigns reads and clears). Always-on sharing is a
 * rule under Reach → Automatic (backlog bf819be4).
 */
function NewCampaignMenu() {
  const navigate = useNavigate();
  return (
    <AgentFirstButton
      action="campaign"
      label="New campaign"
      onSelf={() => navigate('/campaigns?new=dated')}
      footer={(close) => <CampaignMenuNote close={close} />}
    />
  );
}

/** Under New campaign ▾: always-on sharing isn't a campaign (Prototype). */
export function CampaignMenuNote({ close }: { close: () => void }) {
  const navigate = useNavigate();
  return (
    <span className="af-menu-note">
      Sharing every new article? That’s a sharing rule, in{' '}
      <button
        type="button"
        className="ink-link-btn"
        onClick={() => {
          close();
          navigate('/reach/automatic');
        }}
      >
        Social › Automatic
      </button>
      .
    </span>
  );
}

/**
 * Campaigns, top level (backlog bf819be4, Prototype): planned work with a start
 * and an end, which can span the site and the channels. It comes with Pro, and
 * the menu shows it only where Reach is (AppShell); opened directly elsewhere,
 * it says so.
 */
export function CampaignsSection({ children }: { children: React.ReactNode }) {
  const { reachOn } = useAddons();
  // The Campaign role works in the campaigns they're on; they don't start new ones.
  const { selectedAccount, accountId } = useAccount();
  // No campaigns yet: the screen shows one card with one way in, so no tabs
  // and no second New campaign up here (Prototype, feedback 10cb78ba).
  const { data } = useQuery<{ campaignList: unknown[] }>(CAMPAIGN_LIST, {
    variables: { accountId },
    skip: !accountId || !reachOn,
    fetchPolicy: 'cache-and-network',
  });
  const none = Boolean(data && data.campaignList.length === 0);
  const canStart = reachOn && !selectedAccount?.campaignRole && !none;
  return (
    <SectionShell
      title="Campaigns"
      right={canStart ? <NewCampaignMenu /> : null}
      // The board, every campaign on one timeline, and their results added up
      // (TinyCMS UX optimization review, 2026-10-23).
      tabs={
        reachOn && !none
          ? [
              { label: 'Campaigns', to: '/campaigns', exact: true },
              { label: 'Calendar', to: '/campaigns/calendar' },
              { label: 'Analytics', to: '/campaigns/analytics' },
            ]
          : []
      }
    >
      {reachOn ? (
        children
      ) : (
        <div className="ink-section-pad">
          <SectionLocked
            title="Campaigns come with Pro"
            lede="Plan a launch or a sale with its own dates: a landing page, a sign-up form and the posts for it, all approved by you."
            points={[
              'Your agent drafts the plan, the page and the posts',
              'One timeline from first post to last',
              'Results: visits and sign-ups from the campaign',
            ]}
          >
            <SeeProCta />
          </SectionLocked>
        </div>
      )}
    </SectionShell>
  );
}

export function ReachSection({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { reachOn } = useAddons();

  return (
    <SectionShell
      title="Social"
      right={
        reachOn && pathname.startsWith('/reach/automatic') ? (
          <AgentFirstButton
            action="rule"
            label="New rule"
            onSelf={() => navigate('/reach/automatic?new=always')}
          />
        ) : null
      }
      tabs={
        reachOn
          ? [
              { label: 'Calendar', to: '/reach/calendar' },
              // Every post, past and upcoming, in one table (backlog 4e7c78c8).
              { label: 'Posts', to: '/reach/posts' },
              // Always-on sharing rules (Prototype): campaigns moved to the top level.
              { label: 'Automatic', to: '/reach/automatic' },
              { label: 'Channels', to: '/reach/channels' },
              { label: 'Analytics', to: '/reach/analytics' },
            ]
          : []
      }
    >
      {reachOn ? (
        children
      ) : (
        <div className="ink-section-pad">
          <SectionLocked
            title="Social comes with Pro"
            lede="Your agent turns what you publish into social posts and campaigns. Nothing goes out until you approve it."
            points={[
              'Posts for X, LinkedIn, Threads and more, drafted from each article',
              'Campaigns built around what you publish',
              'One calendar where you approve or skip every post',
            ]}
          >
            <SeeProCta />
          </SectionLocked>
        </div>
      )}
    </SectionShell>
  );
}
